import { ChangeEvent } from 'react'

export type WhitespaceNotice = 'notAllowed' | 'removed' | undefined

const INSERT_FROM_OUTSIDE = [
  'insertFromPaste',
  'insertFromDrop',
  'insertReplacementText',
]

export const containsWhitespace = (value: string): boolean => /\s/.test(value)

export const removeWhitespace = (value: string): string =>
  value.replace(/\s/g, '')

export const isWhitespaceKey = (key: string): boolean =>
  key.length === 1 && /\s/.test(key)

export const whitespaceNoticeOf = (
  inputValue: string,
  inputType?: string,
  previousValue?: string
): WhitespaceNotice => {
  if (!containsWhitespace(inputValue)) {
    return undefined
  }
  if (inputType !== undefined) {
    return INSERT_FROM_OUTSIDE.includes(inputType) ? 'removed' : 'notAllowed'
  }
  return previousValue !== undefined &&
    inputValue.length - previousValue.length > 1
    ? 'removed'
    : 'notAllowed'
}

export type PasswordInput = {
  value: string
  notice: WhitespaceNotice
}

export const applyPasswordInput = (
  inputValue: string,
  inputType?: string,
  previousValue?: string
): PasswordInput => ({
  value: removeWhitespace(inputValue),
  notice: whitespaceNoticeOf(inputValue, inputType, previousValue),
})

export const applyPasswordInputEvent = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  previousValue: string
): PasswordInput => {
  const { nativeEvent } = event
  return applyPasswordInput(
    event.target.value,
    nativeEvent instanceof InputEvent ? nativeEvent.inputType : undefined,
    previousValue
  )
}

export type WhitespaceNoticeView = {
  messageId?: string
  tone?: 'warning' | 'info'
}

const WHITESPACE_NOTICE_VIEW: Record<
  Exclude<WhitespaceNotice, undefined>,
  Required<WhitespaceNoticeView>
> = {
  notAllowed: { messageId: 'password.whitespaceNotAllowed', tone: 'warning' },
  removed: { messageId: 'password.whitespaceRemoved', tone: 'info' },
}

export const whitespaceNoticeViewOf = (
  notice: WhitespaceNotice
): WhitespaceNoticeView => (notice ? WHITESPACE_NOTICE_VIEW[notice] : {})
